import { useQuery } from '@tanstack/react-query';
import { fetchProductivityBreakdown, type BreakdownDimension } from '@apis/productivity';
import type { IProductivityRange } from '../utils/productivityRange';

/**
 * One breakdown dimension's rows for a site + range, keyed by dimension so
 * each tab caches independently and switching back is instant. The open tab's
 * rows normally arrive with the period request, which seeds this cache — so
 * this waits (`periodFetching`) while a period request is on the wire, for
 * whatever reason it went out, then finds the rows already there, freshly
 * stamped, and asks the tab endpoint only for a tab the period did not carry. Gated on site + range so a `custom` timeframe waits for both ends.
 * Across a timeframe change the same tab's previous rows stay on hand
 * (`isPlaceholderData`); another tab's rows never stand in, their columns differ.
 */
export function useProductivityBreakdown(
  dimension: BreakdownDimension,
  siteId: string | undefined,
  range: IProductivityRange | null,
  periodFetching: boolean, // A period request is in flight.
) {
  return useQuery({
    queryKey: ['productivity', 'breakdown', dimension, siteId, range?.dateFrom, range?.dateTo],
    queryFn: ({ signal }) => fetchProductivityBreakdown(siteId!, dimension, range!, signal),
    enabled: !!siteId && !!range && !periodFetching,
    placeholderData: (previous) => (previous?.dimension === dimension ? previous : undefined),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}
